/*
  Split header behaviour:
  - <brand-menu> opens its mega panel on hover (pointer) and on click/keyboard,
    matching the reference storefront while staying operable without a mouse.
  - <brand-sticky-header> publishes --header-height so overlays and anchors line up.
*/

if (!customElements.get('brand-menu')) {
  customElements.define(
    'brand-menu',
    class BrandMenu extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector('details');
        this.summary = this.querySelector('summary');
        if (!this.details || !this.summary) return;

        this.wrapper = this.closest('.brand-header-wrapper');
        this.hoverCapable = window.matchMedia('(hover: hover) and (min-width: 990px)');
        this.closeTimer = null;

        this.onSummaryClick = this.onSummaryClick.bind(this);
        this.onToggle = this.onToggle.bind(this);
        this.onEnter = this.onEnter.bind(this);
        this.onLeave = this.onLeave.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);

        this.summary.addEventListener('click', this.onSummaryClick);
        this.details.addEventListener('toggle', this.onToggle);
        this.addEventListener('mouseenter', this.onEnter);
        this.addEventListener('mouseleave', this.onLeave);
        this.addEventListener('focusin', this.onEnter);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        this.clearTimer();
      }

      clearTimer() {
        if (this.closeTimer) {
          clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }
      }

      onSummaryClick(event) {
        // On hover-capable screens the panel is already open; let the click
        // through to the panel instead of toggling it shut immediately.
        if (this.hoverCapable.matches && this.details.open) {
          event.preventDefault();
          this.close();
        }
      }

      onToggle() {
        this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
        if (!this.wrapper) return;

        if (this.details.open) {
          // Only one panel open at a time.
          this.wrapper.querySelectorAll('brand-menu').forEach((menu) => {
            if (menu !== this) menu.close();
          });
          this.wrapper.classList.add('has-open-menu');
        } else if (!this.wrapper.querySelector('brand-menu details[open]')) {
          this.wrapper.classList.remove('has-open-menu');
        }
      }

      onEnter() {
        if (!this.hoverCapable.matches) return;
        this.clearTimer();
        this.open();
      }

      onLeave() {
        if (!this.hoverCapable.matches) return;
        this.clearTimer();
        this.closeTimer = setTimeout(() => this.close(), 160);
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.details.open) {
          this.close();
          this.summary.focus();
        }
      }

      onDocumentClick(event) {
        if (this.details.open && !this.contains(event.target)) this.close();
      }

      open() {
        if (!this.details.open) this.details.open = true;
      }

      close() {
        if (this.details.open) this.details.open = false;
      }
    }
  );
}

if (!customElements.get('brand-sticky-header')) {
  customElements.define(
    'brand-sticky-header',
    class BrandStickyHeader extends HTMLElement {
      connectedCallback() {
        this.setHeights();
        this.onResize = this.setHeights.bind(this);
        window.addEventListener('resize', this.onResize);

        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(() => this.setHeights());
          this.observer.observe(this);
        }
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        this.observer?.disconnect();
      }

      setHeights() {
        const height = this.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
        document.documentElement.style.setProperty(
          '--brand-header-bottom',
          `${this.getBoundingClientRect().bottom}px`
        );
      }
    }
  );
}
